import { UseQueryResult } from 'react-query'
import { TipoAdjunto } from 'src/enums/EscritosLegales'
import { FormField } from 'src/enums/Form'
import {
  Colegio,
  Comunidad,
  Entidad,
  Materia,
  Municipio,
  Orden,
  OrganoJudicial,
  Pais,
  PartidoJudicial,
  Provincia,
  Sexo,
  SubTipoDocumento,
  TipoDocumento,
  TipoDocumentoIdentificativo,
  TipoIntervencion,
  TipoPersona,
  TipoProcedimiento,
  TipoRepresentacion,
  TipoVia,
  TipologiaProcedimiento
} from 'src/interfaces/Catalog'
import { queryFilter, FilterType as FT } from 'src/utils/queryFilter'
import {
  useFetchColegios,
  useFetchComunidades,
  useFetchEntidades,
  useFetchEntidadesByOrganismoOrigen,
  useFetchMaterias,
  useFetchMunicipios,
  useFetchOrganoJudicial,
  useFetchPais,
  useFetchPartidoJudicial,
  useFetchProcedimientoTipo,
  useFetchProcedimientoTipologia,
  useFetchProvincias,
  useFetchSexo,
  useFetchSubTipoDocumento,
  useFetchTipoDocumento,
  useFetchTipoDocumentoIdentificativo,
  useFetchTipoIntervencion,
  useFetchTipoPersona,
  useFetchTipoRepresentacion,
  useFetchTipoVia
} from './useCatalogoGeneralFetch'

enum FilterType {
  NO_FILTER = 'noFilter',
  EQUAL = 'equal',
  EQUAL_OBJ = 'equal_obj',
  NOT_EQUAL = 'not_equal',
  START_WITH = 'startWith',
  START_WITH_SUBSTRING = 'startWithSubstring',
  MOTA = 'mensajeOrdenTipoAdjunto',
  FOTO = 'funcionOrdenTipoOrgano'
}
interface Queries<T> {
  [name: string]: UseQueryResult<T, unknown>
}
interface Mota {
  tipoMensaje: number
  orden: number
  tipoAdjunto: number
}

const queries: Queries<any> = {}

function setFilter<T, C>(
  field: FormField,
  filterTypes: FilterType | FilterType[],
  filters: C | null = null,
  firstFields: string | string[] = [],
  secondFields: string | string[] = []
): T[] | undefined {
  let listData: T[] | undefined = queries[field].data
  if (filterTypes === FilterType.NO_FILTER || !filters) {
    return listData
  }

  if (!listData || !filters || !firstFields || !secondFields) {
    return []
  }

  const filterArray = Array.isArray(filters) ? filters : [filters]
  const filterTypesArray: FilterType[] =
    typeof filterTypes === 'string' ? [filterTypes] : filterTypes
  const firstFieldsArray = typeof firstFields === 'string' ? [firstFields] : firstFields
  const secondFieldsArray = typeof secondFields === 'string' ? [secondFields] : secondFields

  filterTypesArray.map((filterType: FilterType, index: number) => {
    const filter = filterArray[index]
    const firstField = firstFieldsArray[index]
    const secondField = secondFieldsArray[index]

    if (filter) {
      switch (filterType) {
        case FilterType.EQUAL:
          listData = listData?.filter(
            (data: T) => data[firstField].toString() === filter[secondField].toString()
          )
          break
        case FilterType.NOT_EQUAL:
          listData = listData?.filter(
            (data: T) => data[firstField].toString() !== filter[secondField].toString()
          )
          break
        case FilterType.EQUAL_OBJ:
          if (typeof filter === 'object') {
            listData = listData?.filter((data: T) =>
              data[firstField].some((v: T) => {
                if (filter.some((f: C) => f === v[secondField])) {
                  return true
                }
                return false
              })
            )
          } else {
            listData = listData?.filter((data: T) =>
              data[firstField].some((v: T) => v[secondField] === filter)
            )
          }
          break
        case FilterType.MOTA:
          listData = listData?.filter((data: T) => {
            const dataMota = Array.isArray(data[firstField])
              ? data[firstField]
              : Object.values(data[firstField])
            return dataMota.some(
              (v: Mota) =>
                v.tipoMensaje === filter.tipoMensaje &&
                v.orden === filter.orden &&
                (v.tipoAdjunto === filter.tipoAdjunto || v.tipoAdjunto === TipoAdjunto.AMBOS)
            )
          })
          break
        case FilterType.FOTO:
          listData = listData?.filter((data: any) => {
            return data.funcionOrdenTipoOrgano.some((v: any) => {
              return v.organo === filter.organo.tipoOrgano && v.orden === filter.orden.orden
            })
          })
          break
        case FilterType.START_WITH:
          listData = listData?.filter((data: T) => {
            return (data[firstField] as string).startsWith(String(filter[secondField]))
          })
          break
        case FilterType.START_WITH_SUBSTRING:
          listData = listData?.filter((data: T) =>
            (data[firstField] as string).substring(1).startsWith(filter[secondField])
          )
          break
        default:
          break
      }
    }
    return null
  })
  return listData
}

function useGetComunidades(field: FormField): Comunidad[] | undefined {
  queries[field] = useFetchComunidades(['comunidades'])
  return setFilter<Comunidad, undefined>(field, FilterType.NO_FILTER)
}

function useGetDestinoComunidades(field: FormField): Comunidad[] | undefined {
  queries[field] = useFetchComunidades(['comunidades'])
  const { data } = queries[field]
  return data?.filter((c: Comunidad) => c.lexnet === true)
}

function useGetProvincias(field: FormField, filter: Comunidad | null): Provincia[] | undefined {
  queries[field] = useFetchProvincias(['provincia'])
  return setFilter<Provincia, Comunidad>(
    field,
    FilterType.EQUAL,
    filter,
    'comunidadAutonoma',
    'codigo'
  )
}

function useGetMaterias(field: FormField): Materia[] | undefined {
  queries[field] = useFetchMaterias(['materias'])
  return queries[field].data
}

function useGetColegios(field: FormField, filter: any[]): Colegio[] | undefined {
  queries[field] = useFetchColegios(['colegios'])
  return setFilter<Colegio, any[]>(
    field,
    [FilterType.START_WITH_SUBSTRING, FilterType.EQUAL],
    filter,
    ['codigo', 'tipoRepresentacion'],
    ['codigo', 'id']
  )
}

function useGetPartidosJudiciales(
  field: FormField,
  filter: Provincia | null
): PartidoJudicial[] | undefined {
  queries[field] = useFetchPartidoJudicial(['partido-judicial'])
  return setFilter<PartidoJudicial, Provincia>(
    field,
    FilterType.START_WITH,
    filter,
    'municipio',
    'codigo'
  )
}

function useGetOrganosJudiciales(
  field: FormField,
  filter: any[],
  withOrden = false,
  claseRegistro = false
): OrganoJudicial[] | undefined {
  queries[field] = useFetchOrganoJudicial(['organo-judicial'])

  const filterConfig = withOrden
    ? [FilterType.EQUAL, FilterType.EQUAL_OBJ, FilterType.EQUAL_OBJ]
    : [FilterType.EQUAL, FilterType.EQUAL_OBJ]

  const fields = withOrden
    ? ['codigoPoblacion', 'mensajes', 'ordenes']
    : ['codigoPoblacion', 'mensajes']

  const filterFields = withOrden ? ['municipio', 'codigo', 'orden'] : ['municipio', 'codigo']

  let organoJudicialFiltered = setFilter<OrganoJudicial, any[]>(
    field,
    filterConfig,
    filter,
    fields,
    filterFields
  )

  if (!claseRegistro) {
    organoJudicialFiltered = organoJudicialFiltered?.filter(
      (o: OrganoJudicial) => o.claseRegistro === false
    )
  }

  if (withOrden && organoJudicialFiltered) {
    organoJudicialFiltered = organoJudicialFiltered.map((organo: OrganoJudicial) => {
      if (!organo.ordenes || organo.ordenes.length === 0) {
        return organo
      }

      const ordenesFiltradas = organo.ordenes.filter((orden: Orden) => orden.orden === filter[2])
      return { ...organo, ordenes: ordenesFiltradas }
    })
  }

  return organoJudicialFiltered
}

function useGetTipoPersona(field: FormField): TipoPersona[] | undefined {
  queries[field] = useFetchTipoPersona(['tipo-persona'])
  return setFilter<TipoPersona, undefined>(field, FilterType.NO_FILTER)
}

function useGetMunicipios(
  field: FormField,
  filter: Provincia | null | undefined
): Municipio[] | undefined {
  queries[field] = useFetchMunicipios(['municipios'])
  return setFilter<Municipio, Provincia>(
    field,
    [FilterType.START_WITH],
    filter,
    ['codigo'],
    ['codigo']
  )
}

function useGetTipoDocumentoIdentificativo(
  field: FormField,
  filter: TipoPersona | null
): TipoDocumentoIdentificativo[] | undefined {
  queries[field] = useFetchTipoDocumentoIdentificativo(['tipo-documento-identificativo'])
  return setFilter<TipoDocumentoIdentificativo, TipoPersona>(
    field,
    FilterType.EQUAL,
    filter,
    'codigoTipoPersona',
    'codigo'
  )
}

function useGetTipoDocumentoRLByTipoPersona(
  field: FormField,
  tipoPersona: TipoPersona
): TipoDocumentoIdentificativo[] | undefined {
  queries[field] = useFetchTipoDocumentoIdentificativo(['tipo-documento-identificativo'])
  return queryFilter(queries[field].data, [
    {
      condition: 'OR',
      filters: [
        { key: 'codigo', value: 'N', type: FT.EQUAL },
        { key: 'codigo', value: 'X', type: FT.EQUAL }
      ]
    },
    {
      condition: 'AND',
      filters: [{ key: 'codigoTipoPersona', value: tipoPersona?.codigo, type: FT.EQUAL }]
    }
  ])
}

function useGetEntidades(
  field: FormField,
  provincia: Provincia | null,
  comunidad: Comunidad | null
): Entidad[] {
  queries[field] = useFetchEntidades([field])
  if (queries[field].data) {
    return queryFilter(queries[field].data, [
      {
        condition: 'OR',
        filters: [
          { key: 'provincia', value: provincia?.codigo, type: FT.EQUAL },
          { key: 'comunidadAutonoma', value: comunidad?.codigo, type: FT.EQUAL }
        ]
      }
    ])
  }
  return []
}

function useGetProcedimientoTipologia(field: FormField): TipologiaProcedimiento[] | undefined {
  queries[field] = useFetchProcedimientoTipologia(['tipologia-procedimiento'])
  return setFilter<TipologiaProcedimiento, undefined>(field, FilterType.NO_FILTER)
}

function useGetProcedimientoTipo(
  field: FormField,
  filter: any[],
  isAdaptadoTest: boolean
): TipoProcedimiento[] | undefined {
  queries[field] = useFetchProcedimientoTipo(
    [isAdaptadoTest ? 'procedimiento-tipo-cteage' : 'procedimiento-tipo-lexnet'],
    isAdaptadoTest
  )
  return setFilter<TipoProcedimiento, any[]>(
    field,
    [FilterType.EQUAL, FilterType.EQUAL, FilterType.EQUAL],
    filter,
    ['tipologia', 'tipoOrgano', 'jurisdiccion'],
    ['codigo', 'tipoOrgano', 'orden']
  )
}

function useGetProcedimientoNotPiezaTipo(
  field: FormField,
  filter: any[],
  isAdaptadoTest: boolean
): TipoProcedimiento[] | undefined {
  queries[field] = useFetchProcedimientoTipo(
    [isAdaptadoTest ? 'procedimiento-no-pieza-tipo-cteage' : 'procedimiento-no-pieza-tipo-lexnet'],
    isAdaptadoTest
  )
  return setFilter<TipoProcedimiento, any[]>(
    field,
    [FilterType.NOT_EQUAL, FilterType.EQUAL, FilterType.EQUAL],
    filter,
    ['tipologia', 'tipoOrgano', 'jurisdiccion'],
    ['codigo', 'tipoOrgano', 'orden']
  )
}

function useGetTipoDocumento(field: FormField): TipoDocumento[] | undefined {
  queries[field] = useFetchTipoDocumento(['tipo-documento'])
  return setFilter<TipoDocumento, undefined>(field, FilterType.NO_FILTER)
}

function useGetSubtipoDocumento(field: FormField, filter: any[]): SubTipoDocumento[] | undefined {
  queries[field] = useFetchSubTipoDocumento(['sub-tipo-documento'])
  return setFilter<SubTipoDocumento, any[]>(
    field,
    [FilterType.MOTA],
    filter,
    ['mensajeOrdenTipoAdjunto'],
    ['tipoMensaje']
  )
}

function useGetSexo(field: FormField): Sexo[] | undefined {
  queries[field] = useFetchSexo(['sexo'])
  return queries[field].data
}

function useGetTipoVia(field: FormField): TipoVia[] | undefined {
  queries[field] = useFetchTipoVia([field])
  return queries[field].data
}

function useGetTipoRepresentacion(field: FormField): TipoRepresentacion[] | undefined {
  queries[field] = useFetchTipoRepresentacion([field])
  return queries[field].data
}

function useGetPais(field: FormField): Pais[] | undefined {
  queries[field] = useFetchPais([field])
  return queries[field].data
}

function useGetTipoIntervencion(field: FormField, filter: any[]): TipoIntervencion[] | undefined {
  queries[field] = useFetchTipoIntervencion([field])
  return setFilter<TipoIntervencion, any[]>(field, [FilterType.FOTO], filter, ['orden'], ['organo'])
}

function useGetEntidadesByOrganismoOrigen(field: FormField): Entidad[] | undefined {
  queries[field] = useFetchEntidadesByOrganismoOrigen([field])
  return queries[field].data
}

export {
  useGetColegios,
  useGetComunidades,
  useGetDestinoComunidades,
  useGetEntidades,
  useGetEntidadesByOrganismoOrigen,
  useGetMaterias,
  useGetMunicipios,
  useGetOrganosJudiciales,
  useGetPais,
  useGetPartidosJudiciales,
  useGetProcedimientoNotPiezaTipo,
  useGetProcedimientoTipo,
  useGetProcedimientoTipologia,
  useGetProvincias,
  useGetSexo,
  useGetSubtipoDocumento,
  useGetTipoDocumento,
  useGetTipoDocumentoIdentificativo,
  useGetTipoDocumentoRLByTipoPersona,
  useGetTipoIntervencion,
  useGetTipoPersona,
  useGetTipoRepresentacion,
  useGetTipoVia
}
